// Create Claims V2 model — pure derivations reused from v1 (CreateClaims.jsx) plus the three
// approved V2 changes (split coding, blocked vs review stages, working Ready). No new data/endpoints.
import { readiness, estCharge, payerOf } from "@/lib/claimReadiness";

export { estCharge, payerOf, readiness };

export const NUM = "123456789..."; // 1..12 rendered numerically below
export const BAD_FLAG = new Set(["rare", "red", "yellow", "fail", "warn", "warning", "high", "alert", "error"]);
export const badStatus = (v) => BAD_FLAG.has(String(v).toLowerCase());
export const icdFlagged = (c) => (!!c?.provenance && c.provenance !== "clear") || badStatus(c?.validation?.age) || badStatus(c?.validation?.gender);
export const cptFlagged = (c) => Object.values(c?.flags || {}).some(badStatus);

export const VISIT_TAGS = {
  dictation: "Dictation", document_upload: "Encounter Note/Super Bill", document: "Encounter Note/Super Bill",
  wound_event: "Wound Event", wound: "Wound Event", telemedicine: "Telemedicine", televisit: "Telemedicine",
};
export const titleize = (s = "") => String(s).replace(/[_-]+/g, " ").replace(/\S+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
export const sourceLabel = (n) => VISIT_TAGS[String(n.visit_source || n.source || "").toLowerCase()] || titleize(n.visit_source || n.source || "—");
// A note can carry more than one source (e.g. dictation + wound event). Normalise an
// array or a delimited string into deduped, human labels for the Source column.
export const sourceLabels = (n) => {
  const raw = n?.visit_source ?? n?.source ?? "";
  const parts = Array.isArray(raw) ? raw : String(raw).split(/[,;/|]+/);
  const labels = parts.map((s) => String(s).trim()).filter(Boolean)
    .map((s) => VISIT_TAGS[s.toLowerCase()] || titleize(s));
  return labels.length ? [...new Set(labels)] : ["—"];
};
export const matchOf = (n) => (n.match_source === "roster" ? "roster match" : (n.match_status || "no match"));
export const isMatched = (n) => n.match_source === "roster" || !!n.mrn;
export const posOf = (n) => n.place_of_service?.code || n.pos_code || null;
export const dosAge = (n) => {
  if (!n.date_of_service) return null;
  const d = Math.floor((Date.now() - new Date(n.date_of_service).getTime()) / 86400000);
  return d >= 0 ? d : null;
};

export function claimView(n) {
  const dx = (n.icd_codes || []).map((c, i) => ({ code: String(c.code || ""), num: i + 1, primary: i === 0, flag: icdFlagged(c), inferred: !!c.provenance && c.provenance !== "clear", why: c.rationale || c.description || "" }));
  const byCode = (code) => dx.find((d) => d.code === String(code || ""));
  const lines = (n.cpt_codes || []).map((c) => {
    const ptrs = (c.dx || []).map((d) => { const m = byCode(d.code); return m ? { num: m.num, code: m.code } : null; }).filter(Boolean);
    const mods = (Array.isArray(c.billing_modifiers) ? c.billing_modifiers : Array.isArray(c.modifiers) ? c.modifiers : []).filter(Boolean);
    return { code: String(c.code || ""), mods, ptrs, noPtr: !(c.dx || []).length, desc: String(c.description || c.label || c.long_description || ""), flag: cptFlagged(c), avg: Number(c?.avg_paid?.amt) || 0 };
  });
  const flagged = dx.filter((d) => d.flag).length + lines.filter((l) => l.flag).length;
  return { dx, lines, flagged, noPtr: lines.filter((l) => l.noPtr) };
}

export function bandOf(n, v) {
  if (!v.lines.length && !v.dx.length) return "none";
  if (!payerOf(n)) return "ins";
  if (!n.date_of_service) return "dos";
  if (v.noPtr.length) return "ptr";
  if (v.flagged) return "rev";
  return "ok";
}
export function blockersSet(n, v) {
  const s = new Set();
  if (!v.lines.length && !v.dx.length) s.add("none");
  if (!payerOf(n)) s.add("ins");
  if (!n.date_of_service) s.add("dos");
  if (v.noPtr.length) s.add("ptr");
  if (v.flagged) s.add("rev");
  return s;
}

// V2 stage: split coding (in-progress) from Nothing-coded (blocked); working Ready.
export function stageOf2(e) {
  const noCodes = !e.v.lines.length && !e.v.dx.length;
  if (noCodes && e.n.stage !== "done") return "coding";
  const b = e.band;
  if (["none", "ins", "dos", "ptr"].includes(b)) return "blocked";
  if (b === "rev") return "review";
  const ready = e.n.validation_report?.billing_output?.ready === true;
  return ready ? "ready" : "review"; // gap case → review
}

export function reviewFlags(e) {
  const icds = e.n.icd_codes || [], cpts = e.n.cpt_codes || [];
  return {
    src: icds.some((c) => c.provenance && c.provenance !== "clear"),
    agesex: icds.some((c) => badStatus(c.validation?.age) || badStatus(c.validation?.gender)),
    proc: cpts.some((c) => Object.values(c.flags || {}).some(badStatus)),
  };
}

// First-match group within a stage. Blocked: none→ins→dos→ptr. Review: src→agesex→proc→gap.
export function groupOf(e, stage) {
  if (stage === "blocked") return e.band; // none|ins|dos|ptr
  if (stage === "review") { const f = reviewFlags(e); return f.src ? "src" : f.agesex ? "agesex" : f.proc ? "proc" : "gap"; }
  return stage;
}

export const GROUP_META = {
  none: { label: "Nothing coded", tone: "red", headline: "revQ found nothing to code in this note." },
  ins: { label: "No insurance", tone: "red", headline: "The note has no payer, so the scrubber cannot run payer rules against it and nothing can be billed." },
  dos: { label: "No DOS", tone: "red", headline: "The note has no date of service, so it cannot be submitted." },
  ptr: { label: "No Dx", tone: "red", headline: "A service line carries no diagnosis pointer — with nothing in Box 24E this claim cannot be saved." },
  src: { label: "Diagnosis source", tone: "amber", headline: "revQ inferred a diagnosis — confirm it before this goes out." },
  agesex: { label: "Age / gender", tone: "amber", headline: "A diagnosis doesn't fit the patient's age or sex." },
  proc: { label: "Procedure flags", tone: "amber", headline: "A procedure was flagged — check it before this goes out." },
  gap: { label: "Needs review", tone: "amber", headline: "Passes revQ's checks — not yet marked ready by the pipeline." },
};

// Ordered groups for headings.
export const BLOCKED_ORDER = ["none", "ins", "dos", "ptr"];
export const REVIEW_ORDER = ["src", "agesex", "proc", "gap"];

// revQ's read: headline + per-blocker/flag items with where each is fixed.
export function composeRead(e) {
  const g = groupOf(e, stageOf2(e));
  const headline = GROUP_META[g]?.headline || "Nothing is blocking this claim.";
  const items = [];
  if (e.blk.has("ins")) items.push({ tone: "red", text: "No payer on file", fix: "link" });
  if (e.blk.has("dos")) items.push({ tone: "red", text: "No date of service", fix: "open" });
  e.v.noPtr.forEach((l, i) => items.push({ tone: "red", text: `Line ${i + 1} · ${l.code} has no diagnosis`, fix: "open" }));
  const f = reviewFlags(e);
  const infN = e.v.dx.filter((d) => d.inferred).length;
  if (f.src) items.push({ tone: "amber", text: infN > 1 ? `${infN} inferred diagnoses to confirm` : "1 inferred diagnosis to confirm", fix: "open" });
  if (f.agesex) items.push({ tone: "amber", text: "A diagnosis doesn't fit age / sex", fix: "open" });
  if (f.proc) { const c = (e.n.cpt_codes || []).find((x) => cptFlagged(x)); items.push({ tone: "amber", text: `${c?.code || "A procedure"} has a procedure flag`, fix: "open" }); }
  return { headline, items };
}

export function verbOf(band) {
  if (band === "none") return { kind: "repro", label: "Reprocess" };
  if (band === "ins") return { kind: "ins", label: "Link insurance" };
  if (band === "dos" || band === "ptr") return { kind: "open", label: "Open claim" };
  return { kind: "scrub", label: "Scrub" };
}

// ---- Create Claims V3 (Oct 7): read-only display helpers, API values as returned ----
export const provOf = (n) => n?.claim?.rendering_provider?.name || n?.provider_name || "";
export const provNpiOf = (n) => n?.claim?.rendering_provider?.npi || n?.npi_number || "";
export const isGenerated = (n) => n?.claim_status === "queued";
export const isSigned = (n) => n?.signed === true;
// revQ agent is still coding this note — row must not open.
export const isAgentCoding = (n) => ["processing", "queued"].includes(n?.status) || (!!n?.stage && n.stage !== "done" && !["ready", "failed", "error"].includes(n?.status));
export const isRecoding = (n) => !!n?.recode_status && !["done", "failed"].includes(n.recode_status);
export const CODING_LOCK_MSG = "revQ is still coding this encounter. It opens as soon as coding finishes.";
export const tovOf = (n) => (n?.tov && n.tov !== "NOT SET" ? n.tov : "");
export const posLine = (n) => {
  const p = n?.place_of_service || n?.service_location || {};
  const code = p.code || n?.pos_code;
  const label = p.description || p.name || "";
  return code ? `POS ${code}${label ? ` · ${label}` : ""}` : "";
};
export const matchLabel = (n) =>
  (n?.match_source === "roster" && n?.match_status === "matched") ? "roster match" : String(n?.match_status || "no match").replace(/_/g, " ");
export const generatedLine = (n) => {
  const d = n?.claim_queued_at ? new Date(n.claim_queued_at) : null;
  const when = d && !isNaN(d) ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
  const who = String(n?.claim_generated_by || "").split("@")[0];
  return ["Generated", when].filter(Boolean).join(" ") + (who ? ` · ${who}` : "");
};
export const dosTime = (n) => { const t = Date.parse(n?.date_of_service || ""); return isNaN(t) ? null : t; };
// Note's created_at (UTC) shown in the user's own time zone; title carries the full stamp.
export const createdStamp = (n) => {
  const d = n?.created_at ? new Date(n.created_at) : null;
  if (!d || isNaN(d)) return null;
  const short = d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const full = d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return { short: `Created ${short}`, full: `Created ${full}` };
};
